'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Canned Tuna and Salmon: Double Exposure"
      author="MicroPlastics Editorial Team"
      date="July 11, 2025"
      readTime="9 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="Microplastics in canned tuna and salmon, fish + can liner combined"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        Canned tuna and salmon are uniquely problematic: they combine
        <strong> fish-borne microplastics</strong> with <strong>epoxy/BPA can
        liner leaching</strong> over the full shelf life. Multiple studies
        have found microplastics in 100% of canned seafood samples tested,
        with bluefin tuna concentrating the most due to bioaccumulation up
        the food chain. <strong>Pouch tuna isn&apos;t safer</strong>, the
        plastic-foil laminate adds polyethylene migration. Cleanest options:
        <strong> glass-jarred wild salmon (Wild Planet glass, Vital Choice
        glass)</strong> or fresh wild-caught fish from a local fishmonger.
        Choose smaller fish (sardines, anchovies, mackerel) over large
        predators when canned is unavoidable.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Canned seafood combines fish-borne microplastics (gut, gills, muscle) with can-liner migration.</>,
          <>Larger predator fish (bluefin tuna, swordfish, mackerel) bioaccumulate microplastics + mercury, limit to once/month max.</>,
          <>Smaller fish (sardines, anchovies, small albacore, light skipjack tuna) have lower bioaccumulation.</>,
          <>&ldquo;BPA-free&rdquo; liners do <em>not</em> just swap in BPS, but the claim only means BPA wasn&apos;t added on purpose.</>,
          <>Cleanest: glass-jarred wild salmon (Wild Planet, Vital Choice), fresh fish from a fishmonger.</>,
          <>Pouch tuna isn&apos;t safer, adds PE polyfilm microplastic on top of fish content.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why canned fish is a double-exposure scenario
      </h2>
      <p>
        Most canned-food microplastic discussions focus on the can liner.
        For canned <em>fish</em> specifically, you get two compounding sources:
      </p>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Fish-borne microplastic.</strong> Sea fish ingest plastic from ocean water. Particles concentrate in gut, gills, and to a lesser degree muscle tissue. Canning typically uses whole-fillet pieces (tuna, salmon) or whole small fish (sardines, anchovies).</li>
        <li><strong>Can liner migration.</strong> Steel cans are coated inside with a thin polymer film to prevent corrosion, historically a BPA epoxy, and in most US food cans today a polyester, acrylic or vinyl coating. (Contrary to a claim we used to repeat here, BPA-free cans are <em>not</em> simply lined with BPS: lining-identification studies do not find it. See <Link href="/blog/microplastics-in-canned-food" className="text-[#1E6B47] hover:underline font-medium">our canned food guide</Link> for the full picture.) Over the 2-3 year shelf life the coating gives up both particles and chemistry into the food, accelerated by the packing oil and any acidity.</li>
        <li><strong>Bonus issue: heat sterilisation.</strong> The retort sterilisation process (heating sealed cans to ~120°C) accelerates initial migration of can-liner chemicals into the fish.</li>
      </ol>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Bioaccumulation: smaller fish are cleaner
      </h2>
      <p>
        Microplastics and mercury both concentrate up the food chain. Small
        fish eat plankton (some microplastic); medium fish eat small fish
        (more); large predators eat medium fish (most). This is why
        <strong> sardines, anchovies, mackerel (smaller varieties), and small
        light skipjack tuna</strong> have lower contamination than
        <strong> bluefin tuna, swordfish, and yellowfin</strong>.
      </p>

      <BlogComparisonTable
        caption="Canned fish ranked by combined microplastic + mercury load"
        columns={[
          { key: 'fish', label: 'Fish' },
          { key: 'load', label: 'Bioaccumulation' },
          { key: 'frequency', label: 'Safe frequency (general adult)' },
        ]}
        rows={[
          { fish: 'Wild Alaskan salmon (canned)', load: 'Low-moderate', frequency: '2-3x per week OK' },
          { fish: 'Sardines (canned)', load: 'Low', frequency: '2-3x per week OK' },
          { fish: 'Anchovies (canned/jarred)', load: 'Low', frequency: '2-3x per week OK' },
          { fish: 'Light skipjack tuna (canned)', load: 'Moderate', frequency: '1-2x per week max' },
          { fish: 'Albacore "white" tuna (canned)', load: 'Higher mercury + microplastic', frequency: '1x per week max' },
          { fish: 'Yellowfin tuna (canned/fresh)', load: 'High mercury + microplastic', frequency: '1-2x per month' },
          { fish: 'Bluefin tuna (sushi/raw)', load: 'Highest bioaccumulation', frequency: 'Rarely; avoid for pregnancy' },
          { fish: 'Swordfish, king mackerel, shark', load: 'Highest mercury', frequency: 'Avoid for pregnancy and children' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Brands and packaging ranked
      </h2>

      <BlogComparisonTable
        caption="Canned/jarred fish brands by microplastic exposure"
        columns={[
          { key: 'brand', label: 'Brand / Product' },
          { key: 'packaging', label: 'Packaging' },
          { key: 'safety', label: 'Relative exposure' },
        ]}
        rows={[
          { brand: 'Wild Planet Wild Sockeye Salmon (glass jar)', packaging: 'Glass jar with metal lid', safety: 'Lowest, no can liner at all' },
          { brand: 'Vital Choice Wild Salmon (glass jar)', packaging: 'Glass jar', safety: 'Lowest' },
          { brand: 'Safe Catch (Mercury Tested) Tuna', packaging: 'Tin-free steel; Safe Catch states an organosol resin lining made without BPA', safety: 'Moderate, the only brand here that names its resin; also mercury-tested per fish' },
          { brand: 'Wild Planet Skipjack Tuna', packaging: 'Steel can, no intentional addition of BPA; Wild Planet does not disclose the liner chemistry', safety: 'Moderate, the company dropped its own "BPA-free" label after trace BPA showed up in testing' },
          { brand: 'StarKist Tuna', packaging: 'Steel can, BPA-NI ("non-intent") liner on newer products', safety: 'Moderate' },
          { brand: 'Bumble Bee, Chicken of the Sea', packaging: 'Standard steel can, liner not disclosed', safety: 'Moderate' },
          { brand: 'Tuna in foil pouches (StarKist Premium Pouches, etc.)', packaging: 'PE-laminated foil pouch', safety: 'Higher, adds polyethylene migration' },
          { brand: 'Restaurant raw tuna sushi (especially bluefin)', packaging: 'Often black plastic tray for takeout', safety: 'Highest (mercury + raw + tray)' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Practical guidance
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Choose glass-jarred fish when available.</strong> Wild Planet and Vital Choice both offer glass-jarred wild salmon. Expensive but lowest exposure.</li>
        <li><strong>If buying canned, choose smaller fish.</strong> Sardines and anchovies are nutrient-dense, low-mercury, low-microplastic options often packed in glass-style jars.</li>
        <li><strong>Avoid pouch tuna.</strong> The PE-laminated foil pouch adds plastic exposure. Glass &gt; can &gt; pouch.</li>
        <li><strong>Limit albacore and yellowfin tuna</strong> to 1x/week max. Bluefin should be rare or avoided.</li>
        <li><strong>Skip tuna entirely during pregnancy</strong>, see our{' '}
          <Link href="/blog/microplastics-in-pregnancy" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
            pregnancy guide
          </Link>{' '}
          for safe seafood recommendations.</li>
          <li><strong>Drain liquid before eating</strong> if buying canned, the oil/water carries the most concentrated plasticiser migration.</li>
          <li><strong>Decant remaining fish to glass</strong> if not eating the whole can; storing opened canned fish in the can extends plastic-contact time.</li>
        </ol>

      <p>
        See related:{' '}
        <Link href="/blog/microplastics-in-seafood" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in seafood
        </Link>,{' '}
        <Link href="/blog/microplastics-in-canned-food" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in canned food
        </Link>, and{' '}
        <Link href="/blog/microplastics-in-sushi" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in sushi
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Packaging material. PET, HDPE, PP, PS, multi-layer, glass, aluminum.</>,
          <>Container condition from the photo, scratches, dents, fade.</>,
          <>Product category, fresh, packaged, canned, frozen, takeout.</>,
          <>Use-context flags you log, microwave, heat, reuse, time stored.</>,
          <>Cited research behind the 0–100 risk score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Use the app as a grocery-store second opinion"
        sub="Scan the product, check the packaging score, compare alternatives. The app weighs material, condition, brand, and the cited research."
        buttonLabel="Scan groceries in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Is canned tuna high in microplastics?',
            answer:
              'Canned tuna combines fish-borne microplastics (concentrated through bioaccumulation up the food chain) with migration from the can lining. Larger tuna (bluefin, yellowfin, albacore) have higher exposure than smaller light skipjack tuna or sardines.',
          },
          {
            question: 'Are pouch tuna products safer than canned?',
            answer:
              'No: pouch tuna is generally worse. The PE-laminated foil pouch adds polyethylene microplastic migration on top of the fish-borne particles. Steel cans (especially BPA-free) are slightly cleaner; glass-jarred fish (Wild Planet, Vital Choice) are best.',
          },
          {
            question: 'Which canned fish brand has the lowest microplastics?',
            answer:
              'Wild Planet and Vital Choice offer glass-jarred wild salmon, zero can-liner exposure. Among canned options, Wild Planet, Safe Catch (mercury-tested), and any brand using BPA-NI (non-intent) can liners are better than standard BPA cans. Sardines and anchovies are lower than tuna regardless of brand.',
          },
          {
            question: 'Is BPA-free canned tuna safe?',
            answer:
              'Probably better than BPA, and definitely less studied. It is a myth that BPA-free cans just use BPS instead, studies that identify what cans are actually lined with find polyester, acrylic and vinyl, not BPS. But "BPA-free" only means BPA was not added deliberately: trace bisphenols still migrate from non-BPA cans, and the replacement polymers have nothing like the toxicology record BPA has. Glass-jarred fish is the only option with no liner at all.',
          },
          {
            question: 'How often is it safe to eat canned tuna?',
            answer:
              'For general adults: light skipjack tuna 1-2x per week max, albacore "white" tuna 1x per week max, yellowfin/bluefin tuna 1-2x per month or less. Pregnant women, nursing mothers, and children should avoid high-mercury tuna entirely. Sardines and salmon are safer 2-3x per week.',
          },
          {
            question: 'Can I just eat fresh fish instead?',
            answer:
              'Yes: fresh wild-caught fish from a fishmonger avoids both can liner exposure and the long shelf-life plastic migration. Smaller fish (sardines, mackerel, herring) and wild salmon are the cleanest fresh options. Avoid farmed salmon, which has separate concerns around feed contamination.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Smith M, Love DC, Rochman CM, Neff RA',
            year: 2018,
            title: 'Microplastics in Seafood and the Implications for Human Health',
            publisher: 'Current Environmental Health Reports',
            url: 'https://link.springer.com/article/10.1007/s40572-018-0206-z',
          },
          {
            authors: 'European Food Safety Authority',
            year: 2023,
            title: 'Re-evaluation of bisphenol A (BPA)',
            publisher: 'EFSA Journal',
            url: 'https://www.efsa.europa.eu/en/efsajournal/pub/6857',
          },
          {
            authors: 'US Food and Drug Administration',
            year: 2024,
            title: 'Mercury Levels in Commercial Fish and Shellfish (1990-2012)',
            publisher: 'FDA',
            url: 'https://www.fda.gov/food/metals-and-your-food/mercury-levels-commercial-fish-and-shellfish-1990-2012',
          },
          {
            authors: 'Consumer Reports',
            year: 2022,
            title: 'Canned tuna mercury and BPA testing',
            publisher: 'Consumer Reports',
            url: 'https://www.consumerreports.org/health/food-safety/canned-tuna-mercury-and-bpa-a1051850790/',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
